// Ruteo mínimo con la History API (sin dependencias). El servidor sirve index.html para cualquier ruta que no sea /api,
// así que recargar o abrir un enlace directo funciona.
import { useSyncExternalStore } from 'react';
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';

const EVENTO = 'cotizador:ruta';

function suscribir(cb: () => void): () => void {
  window.addEventListener('popstate', cb);
  window.addEventListener(EVENTO, cb);
  return () => {
    window.removeEventListener('popstate', cb);
    window.removeEventListener(EVENTO, cb);
  };
}

const leer = () => window.location.pathname + window.location.search;

export function usarRuta(): { ruta: string; consulta: URLSearchParams } {
  const completa = useSyncExternalStore(suscribir, leer, () => '/');
  const [ruta, q = ''] = completa.split('?');
  return { ruta: ruta || '/', consulta: new URLSearchParams(q) };
}

/** Una pantalla con cambios sin guardar (Ampliación, 27-oct-2026) pone una guardia: antes de navegar DENTRO de la app se le
 *  pregunta; si dice false, la navegación no pasa (la pantalla pregunta «Guardar / Descartar» y después navega con `forzar`).
 *  Una sola a la vez: la última que se pone manda; quitarla devuelve la de antes solo si es la misma. */
export type Guardia = (destino: string) => boolean;
let guardia: Guardia | null = null;
export function ponerGuardia(g: Guardia): () => void {
  guardia = g;
  return () => {
    if (guardia === g) guardia = null;
  };
}

/** «Ir a arreglar» (verificador del 2-nov): la pantalla de destino enseña su sección al llegar, no el principio de la página. Va en el
 *  estado del historial, no en la dirección: recargar o compartir el enlace no vuelve a mover la pantalla. */
const LLEGAR_A_LA_SECCION = 'llegarALaSeccion';

export function navegar(a: string, op: { reemplazar?: boolean; forzar?: boolean; aLaSeccion?: boolean } = {}): void {
  if (a === leer()) return;
  if (!op.forzar && guardia && !guardia(a)) return;
  const estado = op.aLaSeccion ? { [LLEGAR_A_LA_SECCION]: true } : null;
  if (op.reemplazar) window.history.replaceState(estado, '', a);
  else window.history.pushState(estado, '', a);
  window.dispatchEvent(new Event(EVENTO));
  if (!op.reemplazar) window.scrollTo({ top: 0 });
}

/** ¿Se llegó aquí pidiendo ver la sección? Se responde UNA vez: al volver con «Atrás» o al cambiar de pestaña no se repite. */
export function tomarLlegadaALaSeccion(): boolean {
  const e = window.history.state as Record<string, unknown> | null;
  if (!e?.[LLEGAR_A_LA_SECCION]) return false;
  window.history.replaceState(null, '', window.location.href);
  return true;
}

/** Une una ruta con su patrón: coincidir('/cotizaciones/:id/:seccion', '/cotizaciones/abc/pdf') → { id, seccion }. */
export function coincidir(patron: string, ruta: string): Record<string, string> | null {
  const p = patron.split('/').filter(Boolean);
  const r = ruta.split('/').filter(Boolean);
  if (p.length !== r.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const a = p[i]!;
    const b = decodeURIComponent(r[i]!);
    if (a.startsWith(':')) out[a.slice(1)] = b;
    else if (a !== b) return null;
  }
  return out;
}

type PropsEnlace = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { a: string; children: ReactNode };

export function Enlace({ a, onClick, children, ...resto }: PropsEnlace) {
  const alClic = (ev: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(ev);
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navegar(a);
  };
  return (
    <a href={a} onClick={alClic} {...resto}>
      {children}
    </a>
  );
}
